import { CartLinesUpdateEvent } from '@shopify/events';

const RESET_AFTER = 2400;

async function fetchCart() {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems) {
    await customElements.whenDefined('cart-items-component');
    if (typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();
  }
  const res = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`Cart ${res.status}`);
  return res.json();
}

class NhAddButton extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    this.button = this.querySelector('button');
    this.status = this.querySelector('[role="status"]');
    this.msg = this.querySelector('.nh-add-msg');
    this.button?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
    clearTimeout(this.#timer);
  }

  #setState(state, text = '') {
    if (!this.button) return;
    this.button.dataset.state = state;
    this.button.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    if (this.status) this.status.textContent = text;
  }

  #onClick = async (event) => {
    event.preventDefault();
    if (this.button.dataset.state === 'loading') return;
    clearTimeout(this.#timer);
    if (this.msg) this.msg.textContent = '';

    const id = this.dataset.variantId;
    const quantity = 1;
    this.#setState('loading', 'Wird hinzugefügt');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: id, quantity }],
        promise: deferred.promise,
      })
    );

    const body = new FormData();
    body.append('id', id);
    body.append('quantity', String(quantity));
    if (sectionIds.length) body.append('sections', sectionIds.join(','));

    try {
      const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        body,
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const data = await res.json();
      const didError = Boolean(data.status);
      const cart = await fetchCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError,
          items: cart.items,
          source: 'nh-add-button',
          sourceId: id,
          itemCount: quantity,
          productId: this.dataset.productId,
          sections: data.sections,
        },
      });

      if (didError) throw new Error(data.description || data.message || 'Fehler');

      this.#setState('added', 'Im Warenkorb');
      this.#timer = setTimeout(() => this.#setState('idle'), RESET_AFTER);
    } catch (error) {
      if (!deferred.settled) {
        try { deferred.reject?.(error); } catch (_) {}
      }
      this.#setState('error', 'Nicht möglich');
      if (this.msg) this.msg.textContent = error?.message || 'Bitte versuchen Sie es erneut.';
      this.#timer = setTimeout(() => this.#setState('idle'), 3200);
    }
  };
}

if (!customElements.get('nh-add-button')) customElements.define('nh-add-button', NhAddButton);

class NhMoodFinder extends HTMLElement {
  connectedCallback() {
    this.moods = [...this.querySelectorAll('[data-mood]')];
    this.moods.forEach((btn) => btn.addEventListener('click', () => this.select(btn.dataset.mood, true)));
    this.addEventListener('keydown', this.#onKey);
  }

  #onKey = (event) => {
    const i = this.moods.indexOf(document.activeElement);
    if (i < 0) return;
    let next = -1;
    if (event.key === 'ArrowRight') next = (i + 1) % this.moods.length;
    if (event.key === 'ArrowLeft') next = (i - 1 + this.moods.length) % this.moods.length;
    if (next < 0) return;
    event.preventDefault();
    this.moods[next].focus();
    this.select(this.moods[next].dataset.mood, true);
  };

  select(key, user) {
    const btn = this.moods.find((b) => b.dataset.mood === key);
    if (!btn) return;
    this.moods.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
    this.style.setProperty('--tint', btn.dataset.tint);
    this.querySelectorAll('[data-pick]').forEach((p) => { p.hidden = p.dataset.pick !== key; });
    this.querySelectorAll('[data-shot]').forEach((s) => s.classList.toggle('is-active', s.dataset.shot === key));
    if (user) btn.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }
}

if (!customElements.get('nh-mood-finder')) customElements.define('nh-mood-finder', NhMoodFinder);
